import { jsPDF } from 'jspdf';

export interface ClinicalNoteEntry {
  session_id: string;
  date: string;         // ISO string
  notes: string;
  process_id: string;
  psychologist_name: string;
  psychologist_discharged: boolean;
}

export interface ClinicalHistoryPdfOptions {
  patientName: string;
  patientEmail: string;
  therapyType?: string | null;
  processStatus?: string | null;
  notes: ClinicalNoteEntry[];
}

/** Formatea fecha ISO en español: "05 de enero de 2024" */
function formatDateEs(isoString: string): string {
  try {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(new Date(isoString));
  } catch {
    return isoString;
  }
}

/** Formatea timestamp de exportación: "25 de mayo de 2026 · 14:32" */
function formatExportTimestamp(): string {
  const now = new Date();
  const date = new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(now);
  const time = now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  return `${date} · ${time}`;
}

/**
 * Genera y descarga un PDF con todas las notas clínicas del paciente.
 * Sólo debe llamarse cuando hay una sesión de impersonación activa.
 */
export function exportClinicalHistoryPdf(opts: ClinicalHistoryPdfOptions): void {
  const { patientName, patientEmail, therapyType, processStatus, notes } = opts;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const PAGE_W = 210;
  const PAGE_H = 297;
  const MARGIN_X = 18;
  const CONTENT_W = PAGE_W - MARGIN_X * 2;
  const FOOTER_H = 14; // reserved height at bottom

  // ── Paleta ──────────────────────────────────────────────────────────────────
  const COLOR_PRIMARY: [number, number, number] = [63, 54, 130];   // #3F3682 aprox
  const COLOR_TEXT: [number, number, number] = [30, 30, 30];
  const COLOR_MUTED: [number, number, number] = [110, 110, 110];
  const COLOR_RULE: [number, number, number] = [200, 200, 210];
  const COLOR_BG_HEADER: [number, number, number] = [245, 244, 252];

  let y = 0; // current vertical cursor

  // ── Helpers de dibujo ───────────────────────────────────────────────────────

  const ensureSpace = (needed: number) => {
    if (y + needed > PAGE_H - FOOTER_H - 4) {
      doc.addPage();
      y = 18;
    }
  };

  const hRule = (color: [number, number, number] = COLOR_RULE, thickness = 0.3) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(thickness);
    doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y);
  };

  const writeWrapped = (
    text: string,
    x: number,
    startY: number,
    maxWidth: number,
    fontSize: number,
    color: [number, number, number],
    bold = false,
  ): number => {
    doc.setFontSize(fontSize);
    doc.setTextColor(...color);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    const lines = doc.splitTextToSize(text, maxWidth);
    const lineH = fontSize * 0.4;
    let cy = startY;
    for (const line of lines) {
      ensureSpace(lineH + 2);
      doc.text(line, x, cy);
      cy += lineH + 0.5;
    }
    return cy;
  };

  // ── Header del documento ─────────────────────────────────────────────────────
  y = 18;

  // Fondo suave del header
  doc.setFillColor(...COLOR_BG_HEADER);
  doc.rect(0, 0, PAGE_W, 40, 'F');

  // Título
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...COLOR_PRIMARY);
  doc.text('Historia Clínica', MARGIN_X, y);
  y += 7;

  // Nombre del paciente
  doc.setFontSize(12);
  doc.setTextColor(...COLOR_TEXT);
  doc.text(patientName, MARGIN_X, y);
  y += 5;

  // Email · Tipo · Estado
  const metaParts: string[] = [];
  if (patientEmail) metaParts.push(patientEmail);
  if (therapyType) metaParts.push(therapyType);
  if (processStatus) metaParts.push(processStatus);
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.text(metaParts.join('  ·  '), MARGIN_X, y);
  y += 6;

  // Línea separadora
  hRule(COLOR_PRIMARY, 0.5);
  y += 6;

  // ── Resumen ──────────────────────────────────────────────────────────────────
  const totalNotas = notes.length;
  doc.setFontSize(9);
  doc.setTextColor(...COLOR_MUTED);
  doc.setFont('helvetica', 'normal');
  doc.text(`Total de registros: ${totalNotas}`, MARGIN_X, y);
  y += 8;

  // ── Notas (orden cronológico descendente) ────────────────────────────────────
  const sorted = [...notes].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  sorted.forEach((note, idx) => {
    ensureSpace(28);

    // Número + fecha
    const dateStr = formatDateEs(note.date);
    const label = `${idx + 1}.  ${dateStr}`;
    doc.setFontSize(9.5);
    doc.setTextColor(...COLOR_PRIMARY);
    doc.setFont('helvetica', 'bold');
    doc.text(label, MARGIN_X, y);

    // Psicóloga — alineado a la derecha
    const psiLabel = `Psico ${note.psychologist_name || 'Psicóloga'}${note.psychologist_discharged ? ' (inactiva)' : ''}`;
    doc.setFontSize(8.5);
    doc.setTextColor(...COLOR_MUTED);
    doc.setFont('helvetica', 'italic');
    doc.text(psiLabel, PAGE_W - MARGIN_X, y, { align: 'right' });
    y += 5;

    // Contenido de la nota
    y = writeWrapped(note.notes || '—', MARGIN_X + 4, y, CONTENT_W - 4, 9.5, COLOR_TEXT);
    y += 3;

    // Separador entre notas (excepto la última)
    if (idx < sorted.length - 1) {
      hRule(COLOR_RULE, 0.2);
      y += 4;
    }
  });

  // ── Footer en todas las páginas ───────────────────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    const fy = PAGE_H - 8;
    doc.setFillColor(245, 244, 252);
    doc.rect(0, PAGE_H - 12, PAGE_W, 12, 'F');
    doc.setDrawColor(...COLOR_RULE);
    doc.setLineWidth(0.3);
    doc.line(MARGIN_X, PAGE_H - 12, PAGE_W - MARGIN_X, PAGE_H - 12);

    doc.setFontSize(7.5);
    doc.setTextColor(...COLOR_MUTED);
    doc.setFont('helvetica', 'normal');
    doc.text(`Exportado el ${formatExportTimestamp()}  ·  Documento de uso interno — Psi Mammoliti`, MARGIN_X, fy);
    doc.text(`${p} / ${pageCount}`, PAGE_W - MARGIN_X, fy, { align: 'right' });
  }

  // ── Descarga ─────────────────────────────────────────────────────────────────
  const safeName = patientName.replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ\s]/g, '').trim().replace(/\s+/g, '_');
  doc.save(`historia_clinica_${safeName}.pdf`);
}
